import React, { useState, useEffect } from "react";
import SelectInput from "./components/SelectInput";
import ToggleSwitch from "./components/ToggleSwitch";
import { getUserId, saveSettings, getSettings } from "../../config/firebaseConfig";
import { useToastStore } from "../../store/toastStore"; // Import toast store

const GeneralSettings: React.FC = () => {
  const [settings, setSettings] = useState({
    appTheme: "Auto",
    clock: "12 hour",
    firstDayOfWeek: "Sunday",
    currency: "INR",
    autoOpenFlight: false,
    showFlightTimesUTC: false,
    pushNotifications: false,
  });

  useEffect(() => {
    const fetchSettings = async () => {
      const userId = await getUserId();
      if (userId) {
        const data = await getSettings(userId);
        if (data) {
          console.log(data);
          setSettings(data);
        }
      }
    };
    fetchSettings();
  }, []);

  const handleSettingChange = (setting: string, value: string) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [setting]: value,
    }));
  };

  const handleToggleChange = (setting: string, value: boolean) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [setting]: value,
    }));
  };

  const handleSave = async () => {
    const userId = await getUserId();
    if (userId) {
      try {
        await saveSettings(userId, settings);

        // Show success toast
        useToastStore.getState().showToast("Settings saved successfully!", "success");

        // Refetch the settings to update UI
        const updatedSettings = await getSettings(userId);
        if (updatedSettings) {
          setSettings(updatedSettings);
        }
      } catch (error) {
        useToastStore
          .getState()
          .showToast("Error saving settings. Please try again.", "error");
        console.error("Error saving settings:", error);
      }
    }
  };

  return (
    <div className="mt-4 sm:ml-64">
      <div className="bg-white p-6 rounded-xl">
        <p className="text-grease-100 text-md font-medium mb-6">General Settings</p>
        <div className="grid md:grid-cols-2 grid-cols-1 gap-6">
          <ToggleSwitch
            label="Automatically open next open flight after submitting loads"
            description="The next open request for your airline will automatically be opened after submitting loads."
            checked={settings.autoOpenFlight}
            onChange={(value: boolean) => handleToggleChange("autoOpenFlight", value)}
          />
          <ToggleSwitch
            label="Show flight times on open requests in UTC"
            description="Enable to show the flight times of open requests in UTC."
            checked={settings.showFlightTimesUTC}
            onChange={(value: boolean) => handleToggleChange("showFlightTimesUTC", value)}
          />
        </div>

        <p className="text-grease-100 text-md font-medium my-6">App Settings</p>
        <div className="grid md:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-6">
          <SelectInput
            label="App Theme"
            options={["Auto", "Light", "Dark"]}
            selectedValue={settings.appTheme}
            onChange={(value: string) => handleSettingChange("appTheme", value)}
          />
          <SelectInput
            label="Clock"
            options={["12 hour", "24 hour"]}
            selectedValue={settings.clock}
            onChange={(value: string) => handleSettingChange("clock", value)}
          />
          <SelectInput
            label="First Day of the Week"
            options={["Sunday", "Monday"]}
            selectedValue={settings.firstDayOfWeek}
            onChange={(value: string) => handleSettingChange("firstDayOfWeek", value)}
          />
          <SelectInput
            label="Currency"
            options={["INR", "USD", "EUR", "GBP"]}
            selectedValue={settings.currency}
            onChange={(value: string) => handleSettingChange("currency", value)}
          />
        </div>

        <p className="text-grease-100 text-md font-medium my-6">Notification</p>
        <ToggleSwitch
          label="Push Notifications"
          checked={settings.pushNotifications}
          onChange={(value: boolean) => handleToggleChange("pushNotifications", value)}
        />

        <div className="flex items-center justify-end gap-4">
          <button className="p-4 w-32 rounded-lg bg-lightgray-100 text-lightgray-200 text-xs mt-6">
            CANCEL
          </button>
          <button
            onClick={handleSave}
            className="p-4 w-32 rounded-lg bg-royalblue-100 text-white shadow-button text-xs mt-6"
          >
            SAVE
          </button>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings;
